// WHAT THE SCREENS SAY ABOUT WHAT VISITORS SEE, for a page and for a group.
//
// The API answers three things about a page (`served`, `published`,
// `changed` — see PagePublished in the api schema), and the page's own screen
// turns them into a sentence and a button. Every other place that names a page
// names it BESIDE its translations: the listing draws one row per group with a
// chip per language, and a page's screen lists its other languages in a table.
// Both used to say nothing about publishing for anybody but the row's head, so
// a Spanish draft with edits waiting to be published sat behind an English
// page that was published and unchanged, and nothing on either screen said
// so. Finding out meant opening the Spanish page.
//
// One module, because the word has to be the same word everywhere: `Draft` for
// a page nobody can reach yet, `Changes` for a draft that has moved on from
// what is served, and nothing for the common case — published and identical,
// or a row that predates publishing and serves itself.
import React from "react";
import { localeOf } from "./list/groups";

export const DRAFT = "draft";
export const CHANGES = "changes";

// `served === false` and not a falsy check, so a row loaded without the field
// (an older cache, a test fixture) reads as nothing rather than as "Draft".
export const publishStateOf = (page) => {
  if (!page) {
    return null;
  }

  if (page.served === false) {
    return DRAFT;
  }

  return page.published && page.changed ? CHANGES : null;
};

const LABEL = {
  [DRAFT]: "Draft",
  [CHANGES]: "Changes",
};

const CLASS = {
  [DRAFT]: "badge-secondary",
  [CHANGES]: "badge-warning",
};

// The sentence for one page, in the words the page's own screen uses.
const TITLE = {
  [DRAFT]: "Not published — visitors get nothing at this route until it is",
  [CHANGES]: "The draft has changes that are not published",
};

// The same sentence for a group, naming the languages it is true of — the
// row's badge speaks for every language on the row, and which one is behind
// is the thing an author wants to know without opening any of them.
const GROUP_TITLE = {
  [DRAFT]: (codes) => `Not published in ${codes.join(", ")} — visitors get nothing at that route until it is`,
  [CHANGES]: (codes) => `Unpublished changes in ${codes.join(", ")}`,
};

export const PublishBadge = ({ state, title }) =>
  state ? (
    <span className={`badge ${CLASS[state]} mr-1`} title={title || TITLE[state]}>
      {LABEL[state]}
    </span>
  ) : null;

// One page's badge, or nothing.
export const publishBadge = (page) => <PublishBadge state={publishStateOf(page)} />;

/**
 * The languages of a group in each state, in the order the group is read in.
 *
 * Both states can be true of one group at once — a language nobody has
 * published yet beside one whose draft has moved on — and a row that collapsed
 * them into one word would be wrong about one of them, so each is answered
 * separately and the row draws both.
 */
export const groupPublishStates = (members, site) => {
  const states = { [DRAFT]: [], [CHANGES]: [] };

  for (const member of members || []) {
    const state = publishStateOf(member);

    if (state) {
      states[state].push(localeOf(site, member));
    }
  }

  return states;
};

// The badges for a group, one per state that is true of any language in it.
// A group of one is titled the way the page's own screen would say it — there
// is no language to name that the chip beside it does not already say.
export const GroupPublishBadges = ({ members, site }) => {
  const states = groupPublishStates(members, site);
  const single = (members || []).length <= 1;

  return (
    <>
      {[DRAFT, CHANGES]
        .filter((state) => states[state].length > 0)
        .map((state) => (
          <PublishBadge
            key={state}
            state={state}
            title={single ? TITLE[state] : GROUP_TITLE[state](states[state])}
          />
        ))}
    </>
  );
};

// The tail a language chip's tooltip carries when that language is in a state
// worth saying, so the chip explains its colour.
export const chipNoteOf = (state) => {
  switch (state) {
    case DRAFT:
      return " — not published";
    case CHANGES:
      return " — unpublished changes";
    default:
      return "";
  }
};

// The colour a chip takes for its publish state, matching the badge that says
// the same thing on the row. Nothing for the quiet state, so the caller's own
// default applies.
export const chipClassOf = (state) => (state ? CLASS[state] : "");
